/**
 * Las afirmaciones que una ficha de evidencia puede respaldar.
 *
 * POR QUÉ UNA LISTA CERRADA Y NO TEXTO LIBRE
 *
 * Es lo que hace que una cita se pueda COMPROBAR. Con texto libre, «esto lo
 * respalda la literatura sobre volumen» es una frase que nadie puede verificar
 * ni contradecir: no se sabe qué decisión respalda, ni si la ficha que se cita
 * habla de eso. Con identificadores, el motor puede preguntar «¿qué hay para
 * `volume.weekly_bands`?» y recibir un conjunto, y `validateCitations` puede
 * rechazar una cita que apunte a una ficha que no respalda esa afirmación.
 *
 * Es la misma decisión que llevó a que los ejercicios se referencien por `slug`
 * y no por nombre (ADR-024), y por el mismo motivo: un identificador se valida,
 * una cadena se parece.
 *
 * CADA AFIRMACIÓN CORRESPONDE A UNA DECISIÓN QUE EL MOTOR TOMA HOY
 *
 * No hay ninguna abstracta. Si nadie decide nada con ella, no está: una
 * afirmación que no cruza ninguna decisión es un tema de conversación, y
 * llenar esta lista de temas convertiría la base de evidencia en una
 * biblioteca que nadie consulta.
 *
 * AÑADIR UNA AFIRMACIÓN NO ES GRATIS. Significa que hay una decisión del motor
 * que la evidencia puede matizar, y el §8 separa eso de cambiar el código: «una
 * fuente nueva puede matizar una decisión sin cambiar código». Lo que esta
 * lista fija es DÓNDE puede entrar ese matiz.
 */

export const EVIDENCE_CLAIMS = [
  // --- volumen ------------------------------------------------------------
  /** Cuántas series semanales por músculo son razonables. `volume.ts`. */
  'volume.weekly_bands',
  /** Cuánto puede subir el volumen de una semana a la siguiente. `safety/limits.ts`. */
  'volume.weekly_change',
  /** Repartir el volumen de un músculo en más sesiones cambia el resultado. */
  'volume.frequency',

  // --- intensidad y esfuerzo ----------------------------------------------
  /** Qué RIR prescribir, y cuánto acercarse al fallo. `phase-params.ts`. */
  'effort.proximity_to_failure',
  /** Qué rango de repeticiones usar para hipertrofia. */
  'effort.rep_ranges',
  /** Cuánto descansar entre series. `exercises.ts`, `defaultRestSeconds`. */
  'effort.rest_between_sets',

  // --- progresión ---------------------------------------------------------
  /** Cómo progresar: doble progresión, carga, repeticiones. `load/`. */
  'progression.strategy',
  /** Cuándo un estancamiento es real y qué hacer. `load/stall.ts`. */
  'progression.stall_response',

  // --- fatiga -------------------------------------------------------------
  /** Cada cuánto descargar y cuánto recortar. `deload.ts`. */
  'fatigue.deload_timing',
  /** Qué señales indican fatiga acumulada. `readiness/`. */
  'fatigue.readiness_signals',

  // --- selección de ejercicios --------------------------------------------
  /** Qué hace a un ejercicio buena elección para un músculo. `substitution.ts`. */
  'exercise.selection_criteria',
  /** Si el énfasis en posición estirada cambia el resultado. `stretchEmphasis`. */
  'exercise.stretch_emphasis',
  /** Básicos frente a aislamientos, y en qué orden. `ordenarSesion`. */
  'exercise.compound_vs_isolation',

  // --- estructura del bloque ----------------------------------------------
  /** Cuánto dura un bloque útil y cuándo conviene cambiarlo. `block-phase.ts`. */
  'block.duration',
  /** Especializar: concentrar volumen en unos músculos a costa de otros. */
  'block.specialization',
  /** Volver tras inactividad: cuánto se conserva y a qué ritmo se reintroduce. */
  'block.readaptation',

  // --- técnicas avanzadas -------------------------------------------------
  /** Drop sets, rest-pause: cuándo aportan y cuándo solo añaden fatiga. */
  'technique.advanced_sets',
] as const;

export type EvidenceClaim = (typeof EVIDENCE_CLAIMS)[number];

const CONOCIDAS = new Set<string>(EVIDENCE_CLAIMS);

/** Si una cadena es una afirmación del vocabulario. */
export function isEvidenceClaim(valor: string): valor is EvidenceClaim {
  return CONOCIDAS.has(valor);
}

/**
 * Afirmaciones sobre CAMBIO SOSTENIDO, que una fuente aguda no puede respaldar.
 *
 * El §8 lo prohíbe expresamente: «no conviertas respuestas agudas, medidas
 * indirectas o hipótesis biomecánicas en demostraciones de hipertrofia a largo
 * plazo». Una medición de activación electromiográfica en una sesión no dice
 * nada sobre cuántas series a la semana hacen crecer un músculo, por muy limpia
 * que sea la medición.
 *
 * Las que NO están aquí sí admiten evidencia aguda con sentido: cuánto
 * descansar entre series es una pregunta sobre lo que pasa dentro de la sesión,
 * y ahí un estudio agudo es el diseño adecuado.
 */
export const CLAIMS_DE_LARGO_PLAZO: readonly EvidenceClaim[] = [
  'volume.weekly_bands',
  'volume.weekly_change',
  'volume.frequency',
  'effort.rep_ranges',
  'progression.strategy',
  'block.duration',
  'block.specialization',
  'block.readaptation',
  'exercise.stretch_emphasis',
  'technique.advanced_sets',
];

const LARGO_PLAZO = new Set<string>(CLAIMS_DE_LARGO_PLAZO);

export function esClaimDeLargoPlazo(claim: string): boolean {
  return LARGO_PLAZO.has(claim);
}

/**
 * Cómo se llama cada afirmación y qué se le pregunta a la literatura.
 *
 * LA PREGUNTA VIVE AQUÍ Y NO EN LA PANTALLA, y no es colocación arbitraria: es
 * lo que se manda a un buscador y lo que queda escrito en el registro de la
 * búsqueda (§8 pide guardar «la pregunta investigada»). Redactada en la UI,
 * cada pantalla que lanzara una revisión preguntaría algo ligeramente distinto
 * y el historial dejaría de ser comparable entre revisiones.
 *
 * Están en castellano porque el registro lo lee una persona. Las CONSULTAS que
 * el modelo escriba en el buscador las decide él, y quedan guardadas aparte en
 * `queries`: son dos cosas distintas y el §8 las pide las dos.
 */
export const CLAIM_LABELS: Record<EvidenceClaim, { titulo: string; pregunta: string }> = {
  'volume.weekly_bands': {
    titulo: 'Series semanales por músculo',
    pregunta:
      '¿Cuántas series semanales por grupo muscular producen hipertrofia en personas entrenadas, y a partir de cuántas deja de compensar?',
  },
  'volume.weekly_change': {
    titulo: 'Cuánto subir el volumen de una semana a otra',
    pregunta:
      '¿Qué incremento semanal de volumen toleran las personas entrenadas sin acumular fatiga que impida progresar?',
  },
  'volume.frequency': {
    titulo: 'Frecuencia por músculo',
    pregunta:
      'A igualdad de volumen semanal, ¿repartirlo en más sesiones por músculo cambia la hipertrofia en personas entrenadas?',
  },
  'effort.proximity_to_failure': {
    titulo: 'Cuánto acercarse al fallo',
    pregunta:
      '¿Qué proximidad al fallo (RIR) maximiza la hipertrofia sin comprometer la recuperación en personas entrenadas?',
  },
  'effort.rep_ranges': {
    titulo: 'Rangos de repeticiones',
    pregunta:
      '¿Difiere la hipertrofia entre rangos de repeticiones bajos, medios y altos cuando el esfuerzo se iguala?',
  },
  'effort.rest_between_sets': {
    titulo: 'Descanso entre series',
    pregunta:
      '¿Cuánto descanso entre series conviene para el volumen efectivo y la hipertrofia en trabajo de fuerza?',
  },
  'progression.strategy': {
    titulo: 'Cómo progresar',
    pregunta:
      '¿Qué estrategias de progresión —doble progresión, carga, repeticiones— sostienen mejor el progreso a largo plazo en personas entrenadas?',
  },
  'progression.stall_response': {
    titulo: 'Qué hacer ante un estancamiento',
    pregunta:
      '¿Qué respuesta a un estancamiento de rendimiento en un ejercicio funciona mejor: bajar carga, cambiar ejercicio, descargar o mantener?',
  },
  'fatigue.deload_timing': {
    titulo: 'Cada cuánto descargar',
    pregunta:
      '¿Cada cuántas semanas de acumulación conviene una descarga, y cuánto hay que recortar para que sirva?',
  },
  'fatigue.readiness_signals': {
    titulo: 'Señales de fatiga acumulada',
    pregunta:
      '¿Qué señales subjetivas y de rendimiento predicen fatiga acumulada que justifique reducir carga o volumen?',
  },
  'exercise.selection_criteria': {
    titulo: 'Qué hace bueno a un ejercicio',
    pregunta:
      '¿Qué características de un ejercicio determinan su eficacia para hipertrofiar un músculo concreto?',
  },
  'exercise.stretch_emphasis': {
    titulo: 'Énfasis en posición estirada',
    pregunta:
      '¿Los ejercicios que cargan el músculo en posición alargada producen más hipertrofia que los que lo cargan acortado?',
  },
  'exercise.compound_vs_isolation': {
    titulo: 'Básicos frente a aislamientos',
    pregunta:
      '¿Aportan los ejercicios de aislamiento hipertrofia adicional sobre los básicos cuando el volumen se iguala?',
  },
  'block.duration': {
    titulo: 'Duración de un bloque',
    pregunta:
      '¿Cuánto debe durar un bloque de entrenamiento antes de que convenga cambiar su estructura?',
  },
  'block.specialization': {
    titulo: 'Especialización',
    pregunta:
      '¿Concentrar volumen en pocos músculos reduciendo el del resto produce más hipertrofia en los priorizados?',
  },
  'block.readaptation': {
    titulo: 'Volver tras inactividad',
    pregunta:
      '¿Cuánta masa y fuerza se pierden tras semanas sin entrenar, y a qué ritmo conviene reintroducir el volumen?',
  },
  'technique.advanced_sets': {
    titulo: 'Técnicas avanzadas',
    pregunta:
      '¿Aportan los drop sets y el rest-pause hipertrofia adicional frente a series convencionales igualadas en volumen?',
  },
};
